// src/services/api/endpoints/buildings.ts
import {
  Building,
  BuildingSearchRequest,
  BuildingPaginatedResponse,
  BuildingCreateRequest,
  BuildingUpdateRequest,
  BuildingMatchRequest,
  BuildingMatchResponse,
} from '../../../types';

import { API_BASE_URL, fetchWithTokenRefresh } from '../client';
import { 
  generateCacheKey,
  getCacheItem,
  setCacheItem,
  invalidateCache,
} from '../cache';

// ============================================================================
// Building Operations
// ============================================================================

/**
 * Searches for buildings with optional filters and pagination
 * @param token Firebase authentication token
 * @param params Search parameters (name, page, limit)
 * @returns Promise containing paginated building results
 */
export const searchBuildings = async (
  token: string,
  params?: BuildingSearchRequest
): Promise<BuildingPaginatedResponse> => {
  try {
    // Build query string
    const queryParams = new URLSearchParams();
    if (params?.name) queryParams.append('name', params.name);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    
    const queryString = queryParams.toString();
    const url = `${API_BASE_URL}/buildings/search${queryString ? `?${queryString}` : ''}`;
    const cacheKey = generateCacheKey(url);
    
    // Check cache
    const cachedData = getCacheItem<BuildingPaginatedResponse>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response
    setCacheItem(cacheKey, data);
    
    return data;
  } catch (error) {
    console.error('Error searching buildings:', error);
    throw error;
  }
};

/**
 * Fetches all buildings (convenience function that searches with no filters and large limit)
 * @param token Firebase authentication token
 * @returns Promise containing array of buildings
 */
export const listBuildings = async (token: string): Promise<Building[]> => {
  try {
    // Get all buildings by requesting with max limit (backend allows max 100)
    const response = await searchBuildings(token, { page: 1, limit: 100 });
    return response.items;
  } catch (error) {
    console.error('Error listing buildings:', error);
    throw error;
  }
};

/**
 * Fetches a specific building by ID
 * @param token Firebase authentication token
 * @param buildingId The building ID
 * @returns Promise containing the building
 */
export const getBuilding = async (token: string, buildingId: string): Promise<Building> => {
  try {
    const url = `${API_BASE_URL}/buildings/${buildingId}`;
    const cacheKey = generateCacheKey(url);
    
    // Check cache
    const cachedData = getCacheItem<Building>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response
    setCacheItem(cacheKey, data);
    
    return data;
  } catch (error) {
    console.error(`Error fetching building ${buildingId}:`, error);
    throw error;
  }
};

/**
 * Creates a new building
 * @param token Firebase authentication token
 * @param buildingData Building data to create
 * @returns Promise containing the created building
 */
export const createBuilding = async (
  token: string,
  buildingData: BuildingCreateRequest
): Promise<Building> => {
  try {
    const url = `${API_BASE_URL}/buildings`;
    
    const data = await fetchWithTokenRefresh(url, token, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(buildingData),
    });
    
    // Invalidate buildings list cache
    invalidateCache(`${API_BASE_URL}/buildings/search`);
    
    return data;
  } catch (error) {
    console.error('Error creating building:', error);
    throw error;
  }
};

/**
 * Matches building names using fuzzy matching algorithm
 * @param token Firebase authentication token
 * @param names Array of building names to match
 * @returns Promise containing match results with confidence levels
 */
export const matchBuildings = async (
  token: string,
  names: string[]
): Promise<BuildingMatchResponse> => {
  try {
    const url = `${API_BASE_URL}/buildings/match`;
    
    const payload: BuildingMatchRequest = { names };
    
    const data = await fetchWithTokenRefresh(url, token, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    
    // No caching for this endpoint - always get fresh matches
    return data;
  } catch (error) {
    console.error('Error matching buildings:', error);
    throw error;
  }
};
